import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Globe, Send, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "General Enquiry",
  "Business & Executive Infrastructure",
  "Behavioral Health & Organizational Consulting",
  "Youth & Community Impact",
  "Publishing, Education & Transformative Media",
  "Schedule a Consultation",
];

const CONTACT_ROWS = [
  { icon: Mail, label: "Email", value: "info@vhesolutionsllc.org", href: "mailto:info@vhesolutionsllc.org", testid: "contact-email-link" },
  { icon: Phone, label: "Phone", value: "(505) 788-7377", href: "tel:+15057887377", testid: "contact-phone-link" },
  { icon: MapPin, label: "Office", value: "1209 Mountain Road Pl NE, Ste. N, Albuquerque, NM 87110", href: "https://maps.google.com/?q=1209+Mountain+Road+Pl+NE+Ste+N+Albuquerque+NM+87110", testid: "contact-address-link" },
  { icon: Globe, label: "Web", value: "vhesolutionsllc.org", href: "https://vhesolutionsllc.org", testid: "contact-website-link" },
];

const inputCls =
  "w-full border border-white/15 bg-navy/60 px-4 py-3 text-sm text-parchment placeholder:text-slate-500 transition-colors duration-300 focus:border-gold focus:outline-none";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", interest: INTERESTS[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim() || null,
        interest: form.interest,
        message: form.message.trim(),
      });
      toast.success("Enquiry received.", {
        description: "Thank you — the Vision-Heirs office will respond shortly.",
      });
      setForm({ name: "", email: "", organization: "", interest: INTERESTS[0], message: "" });
    } catch {
      toast.error("Submission failed.", { description: "Please try again, or email info@vhesolutionsllc.org directly." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" aria-labelledby="contact-heading" className="relative overflow-hidden bg-navy py-24 sm:py-32">
      <div className="blueprint-grid absolute inset-0 opacity-30" aria-hidden="true" />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="crosshair left-6 top-10" aria-hidden="true" />
      <div className="crosshair bottom-10 right-8" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          <div>
            <Reveal>
              <p className="mb-5 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.35em] text-gold/90 sm:text-[11px]" data-testid="contact-sheet-label">
                <span className="inline-block h-px w-10 bg-gold/70" />
                Sheet VHE-06 — Point of Connection
              </p>
              <h2 id="contact-heading" className="font-serif text-3xl font-bold tracking-tight text-parchment sm:text-4xl lg:text-5xl">
                Begin the <em className="gold-text">Conversation</em>
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-slate-300">
                Whether you are architecting a venture, strengthening an organization, or building for the next generation — the blueprint begins here.
              </p>
            </Reveal>
            <div className="mt-10 space-y-6">
              {CONTACT_ROWS.map((row, i) => (
                <Reveal key={row.label} delay={0.08 * i}>
                  <a
                    href={row.href}
                    data-testid={row.testid}
                    target={row.href.startsWith("http") ? "_blank" : undefined}
                    rel={row.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="group flex items-start gap-4"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-navy">
                      <row.icon className="h-5 w-5" strokeWidth={1.6} />
                    </span>
                    <span>
                      <span className="block font-mono text-[9px] uppercase tracking-[0.3em] text-slate-400">{row.label}</span>
                      <span className="mt-1 block text-sm text-parchment transition-colors duration-300 group-hover:text-goldlight sm:text-base">{row.value}</span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              aria-label="Contact form"
              className="relative border border-gold/20 bg-blueprint/70 p-7 backdrop-blur-md sm:p-10"
            >
              <span className="crosshair -left-2.5 -top-2.5" aria-hidden="true" />
              <span className="crosshair -bottom-2.5 -right-2.5" aria-hidden="true" />
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block font-mono text-[9px] uppercase tracking-[0.3em] text-gold/80">Name *</label>
                  <input id="contact-name" data-testid="contact-form-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block font-mono text-[9px] uppercase tracking-[0.3em] text-gold/80">Email *</label>
                  <input id="contact-email" data-testid="contact-form-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@organization.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-organization" className="mb-2 block font-mono text-[9px] uppercase tracking-[0.3em] text-gold/80">Organization</label>
                  <input id="contact-organization" data-testid="contact-form-organization-input" value={form.organization} onChange={set("organization")} placeholder="Organization (optional)" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-interest" className="mb-2 block font-mono text-[9px] uppercase tracking-[0.3em] text-gold/80">Area of Interest</label>
                  <select id="contact-interest" data-testid="contact-form-interest-select" value={form.interest} onChange={set("interest")} className={inputCls}>
                    {INTERESTS.map((i) => <option key={i} value={i} className="bg-navy">{i}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-2 block font-mono text-[9px] uppercase tracking-[0.3em] text-gold/80">Message *</label>
                  <textarea id="contact-message" data-testid="contact-form-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about the vision you are building…" className={`${inputCls} resize-y`} />
                </div>
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={sending}
                className="group mt-7 inline-flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-goldlight via-gold to-goldlight bg-[length:200%_100%] bg-left px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-navy transition-[background-position,transform] duration-500 hover:bg-right disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                {sending ? "Transmitting…" : "Send Enquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
